// The recording strip shown on every workspace page while this device records
// a meeting (BUILD 1 WP-4). shell.tsx mounts it once, under the top bar.
//
// Recording: title, clock, upload backlog, Open and Stop. Saving: how much
// audio is still on this device (and that it waits for the network when
// offline). After a reload it is where an interrupted recording is offered
// back: Resume adds a new take to the same meeting, Stop and save ends it.
// Mounting it installs the recorder's browser wiring and reconciles this
// user's spooled meeting audio once (meetingRecorder.boot). Owner: WP-4.
import { useEffect } from 'react';
import { ResumeRecordingButton } from './meeting-audio-resume';
import { MeetingReminder } from './meeting-reminders';
import { formatCopy, useNamespaceCopy, type NamespaceKey } from '@/i18n/ns';
import meetingsRecordCopy from '@/i18n/ns/meetings-record';
import audioCopy from '@/i18n/ns/meeting-audio-input';
import safetyCopy from '@/i18n/ns/meeting-recording-safety';
import { MeetingRecordingSafety } from './meeting-recording-safety';
import { useWorkspace } from './context';
import { RecordingButton, RecorderError } from './meeting-record-control';
import { formatClock } from './meeting-record-parts';
import { onMeetingRecorderDeps } from './meeting-recorder-deps';
import { installBrowserMeetingRecorder } from './meeting-recorder-runtime';
import {
  meetingRecorder,
  recorderBacklogSeconds,
  recorderElapsedMs,
  useMeetingRecorder,
} from './meeting-recorder';

function RecoveryActions() {
  const copy = useNamespaceCopy(meetingsRecordCopy);
  return (
    <>
      <ResumeRecordingButton />
      <button className="ws-button" onClick={() => void meetingRecorder.stopRecovered()}>
        {copy.stopHere}
      </button>
    </>
  );
}

/**
 * What the screen reader hears: the phase and its notices, never the clock or
 * the upload countdown (those change every second while a meeting records).
 */
function phaseAnnouncement(
  copy: Readonly<Record<NamespaceKey<typeof meetingsRecordCopy>, string>>,
  {
    phase,
    online,
    recovering,
    offline,
    recovery,
  }: { phase: string; online: boolean; recovering: boolean; offline: string; recovery: string },
) {
  if (recovering) return `${copy.recoveryTitle}. ${recovery}`;
  if (phase === 'recording') return copy.recordingNow;
  return online ? copy.sealing : offline;
}

const NOTICE_KEYS = {
  tabEnded: 'tabEnded',
  micEnded: 'micEnded',
  micLost: 'micLost',
  limitReached: 'limitReached',
} as const;

export function MeetingRecordingStrip() {
  const ws = useWorkspace();
  const copy = useNamespaceCopy(meetingsRecordCopy);
  const inputCopy = useNamespaceCopy(audioCopy);
  const safety = useNamespaceCopy(safetyCopy);
  const hasSystem = useMeetingRecorder((state) => state.sources.includes('system'));
  const phase = useMeetingRecorder((state) => state.phase);
  const meetingId = useMeetingRecorder((state) => state.meetingId);
  const title = useMeetingRecorder((state) => state.title);
  const notice = useMeetingRecorder((state) => state.notice);
  const online = useMeetingRecorder((state) => state.upload.online);
  const backlog = useMeetingRecorder(recorderBacklogSeconds);
  const elapsed = useMeetingRecorder(recorderElapsedMs);
  const maxDurationMs = useMeetingRecorder((state) => state.maxDurationMs);
  const storage = useMeetingRecorder((state) => state.localStorage);
  const storageWarning = useMeetingRecorder((state) => state.storageWarning);
  const incomplete = useMeetingRecorder((state) => state.upload.incomplete);
  const userId = ws.user?.id;
  useEffect(() => {
    installBrowserMeetingRecorder();
    if (!userId) return;
    void meetingRecorder.boot(userId);
    return onMeetingRecorderDeps(() => void meetingRecorder.boot(userId));
  }, [userId]);
  if (phase === 'idle') return <MeetingReminder />;
  if (phase === 'error')
    return (
      <div className="ws-recording-strip error">
        <RecorderError />
      </div>
    );
  const open = () => meetingId && ws.openCanvas({ kind: 'meeting', id: meetingId });
  const recovering = phase === 'recovering' || phase === 'interrupted';
  const status = recovering
    ? copy.recoveryTitle
    : phase === 'saving'
      ? online
        ? backlog === null
          ? copy.sealing
          : formatCopy(copy.saving, { seconds: backlog })
        : safety.offline
      : formatCopy(copy.elapsed, { time: formatClock(elapsed) });
  const noticeText =
    notice === 'limitWarning'
      ? formatCopy(copy.limitWarning, {
          minutes: Math.max(1, Math.ceil((maxDurationMs - elapsed) / 60_000)),
        })
      : notice
        ? notice === 'micLost' && hasSystem
          ? inputCopy.lost
          : copy[NOTICE_KEYS[notice]]
        : null;
  const announcement = [
    title ?? copy.record,
    phaseAnnouncement(copy, {
      phase,
      online,
      recovering,
      offline: safety.offline,
      recovery: safety.recoveryTail,
    }),
    noticeText,
    !storage.durable ? safety.memory : storage.state === 'failed' ? safety.failed : null,
    storageWarning && safety[storageWarning],
    incomplete && safety.incomplete,
  ]
    .filter(Boolean)
    .join('. ');
  return (
    <div className="ws-recording-strip" data-phase={phase}>
      <span className="ws-sr-only" role="status">
        {announcement}
      </span>
      <span className={`ws-record-dot${phase === 'recording' ? ' live' : ''}`} aria-hidden="true" />
      <span className="ws-recording-strip-copy" aria-hidden="true">
        <strong>{title ?? copy.record}</strong>
        <span>{status}</span>
        {phase === 'recording' && backlog !== null && backlog > 20 && (
          <span>
            {online ? formatCopy(copy.uploadBacklog, { seconds: backlog }) : safety.offline}
          </span>
        )}
        <MeetingRecordingSafety />
        {noticeText && <span className="ws-recording-notice">{noticeText}</span>}
      </span>
      <span className="ws-recording-strip-actions">
        {meetingId && (
          <button className="ws-text-button" onClick={open}>
            {copy.openRecording}
          </button>
        )}
        {recovering ? <RecoveryActions /> : null}
        {incomplete && (
          <button className="ws-button" onClick={() => void meetingRecorder.retryUploads()}>
            {copy.retryStop}
          </button>
        )}
        {(phase === 'recording' || phase === 'stopping') && <RecordingButton compact />}
      </span>
      <RecorderError />
    </div>
  );
}
